/**
 * Owner: Masa Bayu
 * File: design-tokens.ts
 * Date:Time: 2026-09-13 21:34
 * Description: 22E unified token catalog + resolve against QXDesignSystem.
 */
import { listQXColorRolesInTheme, resolveQXColorRole } from './color-system';
import { isQXColorRole } from './color-role';
import { createQXDesignTokenReference, designTokenReferenceKey, formatQXDesignTokenPath, isQXDesignTokenReference, parseQXDesignTokenPath, } from './design-token';
import { isQXElevationLevel, resolveQXElevationLevel } from './elevation-system';
import { isQXShapeStep, resolveQXShapeStep } from './shape-system';
import { isQXSpacingStep, resolveQXSpacingStep } from './spacing-system';
export const QX_DESIGN_TOKENS_MODEL_VERSION = '1.0.0';
export const QX_DESIGN_TOKENS_FORBIDDEN_KEYS = [
    'react',
    'component',
    'css',
    'className',
    'style',
    'dom',
    'html',
    'var',
    'variables',
    'hex',
    'px',
    'boxShadow',
    'renderer',
    'layout',
    'qxw',
    'qxp',
    'widget',
];
function assertNoForbidden(record, label) {
    for (const key of QX_DESIGN_TOKENS_FORBIDDEN_KEYS) {
        if (key in record) {
            throw new Error(`${label} must not include forbidden field "${key}"`);
        }
    }
}
function normalizeReference(reference) {
    if (typeof reference === 'string') {
        return parseQXDesignTokenPath(reference);
    }
    return createQXDesignTokenReference(reference.group, reference.name);
}
export function createQXDesignTokens(options) {
    assertNoForbidden({ ...options }, 'Design tokens');
    const list = options.references.map((item) => {
        if (typeof item !== 'string') {
            assertNoForbidden({ ...item }, 'Design token reference');
        }
        return normalizeReference(item);
    });
    const seen = new Set();
    for (const reference of list) {
        const key = designTokenReferenceKey(reference);
        if (seen.has(key)) {
            throw new Error(`Duplicate design token reference: ${key}`);
        }
        seen.add(key);
    }
    return Object.freeze({
        modelVersion: QX_DESIGN_TOKENS_MODEL_VERSION,
        references: Object.freeze(list),
    });
}
/**
 * Derive the full token catalog from systems defined on a design system.
 * Does not invent values — only exposes existing semantic identities.
 */
export function createQXDesignTokensFromSystem(system) {
    const references = [];
    for (const role of listQXColorRolesInTheme(system.color)) {
        references.push(createQXDesignTokenReference('color', role));
    }
    for (const style of system.typography.roles) {
        references.push(createQXDesignTokenReference('typography', style.role));
    }
    for (const token of system.spacing.spacings) {
        references.push(createQXDesignTokenReference('spacing', token.step));
    }
    for (const token of system.shape.shapes) {
        references.push(createQXDesignTokenReference('shape', token.step));
    }
    for (const token of system.elevation.elevations) {
        references.push(createQXDesignTokenReference('elevation', token.level));
    }
    for (const token of system.border.borders) {
        references.push(createQXDesignTokenReference('border', token.role));
    }
    return createQXDesignTokens({ references });
}
export function isQXDesignTokens(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }
    const record = value;
    return record.modelVersion === QX_DESIGN_TOKENS_MODEL_VERSION && Array.isArray(record.references);
}
export function resolveQXDesignToken(system, reference) {
    const ref = normalizeReference(reference);
    const path = formatQXDesignTokenPath(ref);
    switch (ref.group) {
        case 'color': {
            if (!isQXColorRole(ref.name)) {
                throw new Error(`Invalid color token name "${ref.name}"`);
            }
            const swatchId = resolveQXColorRole(system.color, ref.name);
            return Object.freeze({ group: 'color', path, role: ref.name, swatchId });
        }
        case 'typography': {
            const style = system.typography.roles.find((item) => item.role === ref.name);
            if (!style) {
                throw new Error(`Typography system does not define role "${ref.name}"`);
            }
            return Object.freeze({ group: 'typography', path, role: ref.name, style });
        }
        case 'spacing': {
            if (!isQXSpacingStep(ref.name)) {
                throw new Error(`Invalid spacing token name "${ref.name}"`);
            }
            const value = resolveQXSpacingStep(system.spacing, ref.name);
            return Object.freeze({
                group: 'spacing',
                path,
                step: ref.name,
                value,
                unit: 'step',
            });
        }
        case 'shape': {
            if (!isQXShapeStep(ref.name)) {
                throw new Error(`Invalid shape token name "${ref.name}"`);
            }
            const value = resolveQXShapeStep(system.shape, ref.name);
            return Object.freeze({
                group: 'shape',
                path,
                step: ref.name,
                value,
                unit: 'step',
            });
        }
        case 'elevation': {
            if (!isQXElevationLevel(ref.name)) {
                throw new Error(`Invalid elevation token name "${ref.name}"`);
            }
            const value = resolveQXElevationLevel(system.elevation, ref.name);
            return Object.freeze({
                group: 'elevation',
                path,
                level: ref.name,
                value,
                unit: 'step',
            });
        }
        case 'border': {
            const border = system.border.borders.find((item) => item.role === ref.name);
            if (!border) {
                throw new Error(`Border system does not define role "${ref.name}"`);
            }
            return Object.freeze({ group: 'border', path, role: ref.name, border });
        }
        default: {
            const exhaustive = ref.group;
            throw new Error(`Unsupported design token group: ${String(exhaustive)}`);
        }
    }
}
/**
 * Assert every catalog reference resolves against the authoritative design system.
 */
export function validateQXDesignTokens(tokens, system) {
    if (!isQXDesignTokens(tokens)) {
        throw new Error('Invalid design tokens');
    }
    assertNoForbidden({ ...tokens }, 'Design tokens');
    for (const reference of tokens.references) {
        if (!isQXDesignTokenReference(reference)) {
            throw new Error('Invalid design token reference in catalog');
        }
        resolveQXDesignToken(system, reference);
    }
    createQXDesignTokens(tokens);
}
export function listQXDesignTokenPaths(tokens) {
    return tokens.references.map((reference) => formatQXDesignTokenPath(reference));
}
export function hasQXDesignToken(tokens, reference) {
    const key = designTokenReferenceKey(normalizeReference(reference));
    return tokens.references.some((item) => designTokenReferenceKey(item) === key);
}
//# sourceMappingURL=design-tokens.js.map